import Route from '@/Enums/Routes/Route.enum';
import BigCalendarIcon from '@/components/Icons/BigCalendarIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { getFieldErrorProps } from '@/helpers/form';
import { duplicateCampaign } from '@/store/api/campaigns.api';
import { duplicateDV360TicketsInsertionOrder } from '@/store/api/dv360Tickets/insertionOrders.api';
import { Button, InputLabel, Modal, TextField } from '@mui/material';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface IDuplicateCampaignForm {
  name: string;
}

interface IProps {
  isOpen: boolean;
  originalCampaignName: string;
  campaignId?: number;
  handleClose: () => void;
  className?: string;
  isDV360?: boolean;
}

const DuplicateCampaignModal = ({
  isOpen,
  className,
  handleClose,
  originalCampaignName,
  campaignId,
  isDV360,
}: IProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const form = useForm<IDuplicateCampaignForm>({
    defaultValues: { name: `${originalCampaignName} COPY` },
    mode: 'onChange',
  });

  const { control, reset, handleSubmit } = form;

  useEffect(() => {
    if (isOpen) {
      reset({ name: `${originalCampaignName} COPY` });
    }
  }, [isOpen, originalCampaignName, reset]);

  const onSubmit = useCallback(
    async ({ name }: IDuplicateCampaignForm) => {
      if (!campaignId) return;

      setLoading(true);
      try {
        if (isDV360) {
          const { id } = await duplicateDV360TicketsInsertionOrder(campaignId, name);
          reset();
          navigate(`${Route.CAMPAIGN_MANAGEMENT}/dv360/${id}`);
        } else {
          const { id } = await duplicateCampaign(campaignId, name);
          reset();
          navigate(`${Route.CAMPAIGN_MANAGEMENT}/${id}`);
        }
        handleClose();
      } catch (error) {
        if (isAxiosError(error)) {
          toast.error(error?.response?.data?.message || 'Unknown Internal Error');
        } else {
          toast.error((error as Error).message || 'Something went wrong');
        }
      } finally {
        setLoading(false);
      }
    },
    [setLoading, campaignId, navigate, handleClose, reset],
  );

  if (!campaignId) {
    return null;
  }

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Modal open={isOpen}>
        <ModalBody
          sx={{ width: '600px', minHeight: '320px', display: 'flex', flexDirection: 'column', overflow: 'visible' }}
          className={className}
        >
          <div>
            <ModalTitleCard titleIcon={<BigCalendarIcon />} handleClose={handleClose} title='Campaign Duplication' />
            <FormProvider {...form}>
              <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
                <InputLabel htmlFor='name'>
                  Name<span className='text-[#fe4033]'>*</span>
                </InputLabel>
                <Controller
                  name='name'
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Please enter campaign name',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder='Enter Campaign Name'
                      id='name'
                      {...getFieldErrorProps(fieldState)}
                    />
                  )}
                />
                <div className='mt-10 flex justify-end gap-[12px]'>
                  <Button color='inherit' variant='outlined' onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button color='primary' variant='contained' type='submit'>
                    Duplicate
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </ModalBody>
      </Modal>
    </LoadingBackdropWrapper>
  );
};

export default DuplicateCampaignModal;
